import { Badge, Nav } from "react-bootstrap";
import styled from "styled-components";

import { ColorAA } from "@bagel-web/components";

// The five Playground steps. Numbered badges communicate
// the suggested order; navigation is free once a source is loaded — until
// then every step but Source is disabled (a source triple is mandatory in
// 2.0).
export enum PlaygroundStep {
  Source = "source",
  AgentConfig = "agent-config",
  Functions = "functions",
  AgentState = "agent-state",
  Runner = "runner",
}

export const PLAYGROUND_STEP_ORDER: ReadonlyArray<PlaygroundStep> = [
  PlaygroundStep.Source,
  PlaygroundStep.AgentConfig,
  PlaygroundStep.Functions,
  PlaygroundStep.AgentState,
  PlaygroundStep.Runner,
];

export const PLAYGROUND_STEP_TITLES: Record<PlaygroundStep, string> = {
  [PlaygroundStep.Source]: "Starting Agent",
  [PlaygroundStep.AgentConfig]: "Agent Config",
  [PlaygroundStep.Functions]: "Functions",
  [PlaygroundStep.AgentState]: "Agent State",
  [PlaygroundStep.Runner]: "Runner",
};

// Per-step status shown on the badge. F1 only distinguishes untouched from
// edited; "invalid" arrives with the editors.
export type PlaygroundStepStatus = "untouched" | "edited";

export type PlaygroundStepStatuses = Record<
  PlaygroundStep,
  PlaygroundStepStatus
>;

export const UNTOUCHED_STEP_STATUSES: PlaygroundStepStatuses = {
  [PlaygroundStep.Source]: "untouched",
  [PlaygroundStep.AgentConfig]: "untouched",
  [PlaygroundStep.Functions]: "untouched",
  [PlaygroundStep.AgentState]: "untouched",
  [PlaygroundStep.Runner]: "untouched",
};

const StepNav = styled(Nav)`
  --bs-nav-link-color: var(--bs-body-color);
  --bs-nav-link-hover-color: var(--bs-body-color);
`;

// The active step's badge in brand colors: AA blue in light mode, AA
// yellow in dark. !important outbids Bootstrap's bg-* rule.
const StepBadge = styled(Badge)<{ $active: boolean }>`
  ${({ $active }) =>
    $active &&
    `
    background-color: ${ColorAA.blue} !important;
    color: ${ColorAA.white};

    [data-bs-theme="dark"] & {
      background-color: ${ColorAA.yellow} !important;
      color: ${ColorAA.black};
    }
  `}
`;

// The edited indicator: a small dot next to the step title. A distinct
// element (not a character in the label) so tests and screen readers can
// address it directly.
const EditedDot = styled.span`
  display: inline-block;
  width: 0.5em;
  height: 0.5em;
  border-radius: 50%;
  background-color: var(--bs-primary);
  vertical-align: middle;
`;

/**
 * The Playground's five numbered step tabs.
 *
 * Follows the react-bootstrap tab idiom used by `AgentHistory`, but with
 * numbered badges instead of icons and a per-step status indicator. Only
 * the nav is rendered here; the caller renders the active step's pane, so
 * step content stays free of tab-mounting concerns.
 */
const PlaygroundStepper = ({
  activeStep,
  onSelectStep,
  stepStatuses = UNTOUCHED_STEP_STATUSES,
  disabledSteps = [],
}: {
  activeStep: PlaygroundStep;
  onSelectStep: (step: PlaygroundStep) => void;
  stepStatuses?: PlaygroundStepStatuses;
  // Steps that cannot be navigated to — every step but Source until a
  // source is loaded.
  disabledSteps?: ReadonlyArray<PlaygroundStep>;
}) => {
  return (
    <StepNav
      variant="tabs"
      activeKey={activeStep}
      onSelect={(eventKey: string | null) => {
        if (eventKey) {
          onSelectStep(eventKey as PlaygroundStep);
        }
      }}
    >
      {PLAYGROUND_STEP_ORDER.map((step, index) => (
        <Nav.Item key={step}>
          <Nav.Link
            eventKey={step}
            disabled={disabledSteps.includes(step)}
            data-testid={`playground-step-${step}`}
          >
            <StepBadge
              pill
              bg="secondary"
              $active={activeStep === step}
              className="me-2"
            >
              {index + 1}
            </StepBadge>
            {PLAYGROUND_STEP_TITLES[step]}
            {stepStatuses[step] === "edited" && (
              <EditedDot
                className="ms-2"
                data-testid={`playground-step-edited-${step}`}
                role="status"
                aria-label={`${PLAYGROUND_STEP_TITLES[step]} has unsaved edits`}
              />
            )}
          </Nav.Link>
        </Nav.Item>
      ))}
    </StepNav>
  );
};

export default PlaygroundStepper;
